/**
 * Ad attribution kept by the site itself, so every booking carries its own
 * click IDs and UTMs into the booking system (no CustomerLabs involvement).
 *
 * On each full page load, `captureAttribution()` reads the landing URL. If it
 * carries any UTM or ad click ID, that visit is recorded as the LAST touch, and
 * also as the FIRST touch when there is none yet (or the stored one is older
 * than FIRST_TOUCH_DAYS). `getAttributionPayload()` turns both into the flat
 * fields the booking API accepts.
 *
 * Only campaign parameters and the landing path are kept: never the full URL,
 * which can hold form values. Storage failures (private mode, blocked storage)
 * are swallowed so attribution can never break a page.
 */

const STORAGE_KEY = "cd_attribution_v1";
const FIRST_TOUCH_DAYS = 90;
const MAX_LEN = 255;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_adset", "utm_content", "utm_term"] as const;
const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid"] as const;

type Touch = Partial<Record<(typeof UTM_KEYS)[number] | (typeof CLICK_KEYS)[number], string>> & {
  ts: string;
  path: string;
};

type Stored = { first?: Touch; last?: Touch };

function read(): Stored {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : {};
  } catch {
    return {};
  }
}

function write(value: Stored): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable: attribution for this visit is simply not kept.
  }
}

function clean(value: string | null): string | undefined {
  const v = value?.trim();
  return v ? v.slice(0, MAX_LEN) : undefined;
}

function readCookie(name: string): string | undefined {
  try {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? clean(decodeURIComponent(match[1])) : undefined;
  } catch {
    return undefined;
  }
}

/** Record this page load's campaign parameters, if it has any. Call once on load. */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const touch: Touch = { ts: new Date().toISOString(), path: window.location.pathname.slice(0, MAX_LEN) };
    let found = false;
    for (const key of [...UTM_KEYS, ...CLICK_KEYS]) {
      const value = clean(params.get(key));
      if (value) {
        touch[key] = value;
        found = true;
      }
    }
    if (!found) return;

    const stored = read();
    const firstAge = stored.first ? Date.now() - Date.parse(stored.first.ts) : Infinity;
    const firstExpired = !(firstAge <= FIRST_TOUCH_DAYS * 864e5);
    write({ first: firstExpired ? touch : stored.first, last: touch });
  } catch {
    // Never let attribution break page load.
  }
}

/**
 * Fields for the booking API. First touch fills the utm_ and click ID columns
 * (the API's existing first-touch convention); the last touch goes in
 * `last_touch`. Meta's own _fbc / _fbp cookies are included when present.
 */
export function getAttributionPayload(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  const { first, last } = read();
  const payload: Record<string, unknown> = {};
  if (first) {
    for (const key of [...UTM_KEYS, ...CLICK_KEYS]) {
      if (first[key]) payload[key] = first[key];
    }
    payload.first_touch_timestamp = first.ts;
    payload.landing_path = first.path;
  }
  if (last && last.ts !== first?.ts) payload.last_touch = last;
  const fbc = readCookie("_fbc");
  const fbp = readCookie("_fbp");
  if (fbc) payload.fbc = fbc;
  if (fbp) payload.fbp = fbp;
  return payload;
}
